import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Info } from "lucide-react"
import { BANKING_SCOPE, type BankingCapabilities, type BankingPermissionTier } from "@prescreen/lib/types"
import { PermissionTierTrack } from "@/components/PermissionTierTrack"
import type { ReactNode } from "react"

function FieldRow({
  htmlFor,
  label,
  tooltip,
  children,
}: {
  htmlFor: string
  label: ReactNode
  tooltip: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:min-w-[200px] sm:max-w-md">
        <span id={htmlFor} className="text-sm font-normal leading-snug text-foreground">
          {label}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="More about this permission"
            >
              <Info className="size-4" strokeWidth={1.5} aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            align="start"
            className="max-w-xs text-left text-xs leading-snug"
          >
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </div>
      {children}
    </div>
  )
}

export type BankingPermissionRowProps = {
  idPrefix: string
  capabilities: BankingCapabilities
  onTierChange: (key: keyof BankingCapabilities, tier: BankingPermissionTier) => void
}

export function JxchangeBankingPermissionRows({
  idPrefix,
  capabilities,
  onTierChange,
}: BankingPermissionRowProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-5">
        {(
          [
            [
              "customers",
              "Customers",
              `Read: ${BANKING_SCOPE.CUSTOMER_READ}. Write: also ${BANKING_SCOPE.CUSTOMER_WRITE}. Covers consumer and business customers via customerType.`,
            ],
            [
              "accounts",
              "Accounts",
              `Read: ${BANKING_SCOPE.ACCOUNT_READ}. Write: also ${BANKING_SCOPE.ACCOUNT_WRITE}. Covers personal and business account opening.`,
            ],
            [
              "transfers",
              "Transfers",
              `Read: ${BANKING_SCOPE.TRANSFER_READ}. Write: also ${BANKING_SCOPE.TRANSFER_WRITE}.`,
            ],
            [
              "transactions",
              "Transactions",
              `Read: ${BANKING_SCOPE.TRANSACTIONS_READ}. Write: also ${BANKING_SCOPE.TRANSACTIONS_WRITE}.`,
            ],
            ["loan", "Loans", `Read: ${BANKING_SCOPE.LOAN_READ}. Write: also ${BANKING_SCOPE.LOAN_WRITE}.`],
          ] as const
        ).map(([key, label, tip]) => (
          <FieldRow
            key={key}
            htmlFor={`${idPrefix}-jx-${key}`}
            label={label}
            tooltip={tip}
          >
            <PermissionTierTrack
              labelledBy={`${idPrefix}-jx-${key}`}
              value={capabilities[key]}
              onChange={(tier) => onTierChange(key, tier)}
            />
          </FieldRow>
        ))}
      </div>
    </div>
  )
}

export function AdminApiBankingPermissionRows({
  idPrefix,
  capabilities,
  onTierChange,
}: BankingPermissionRowProps) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Each row adds matching OAuth scopes (for example{" "}
        <code className="rounded bg-muted px-0.5">{BANKING_SCOPE.CUSTOMER_READ}</code>) and Jack Henry RDC URLs
        used for IdP tokens.
      </p>
      <div className="space-y-5">
        {(
          [
            [
              "rdcRequests",
              "RDC requests",
              `Grants ${BANKING_SCOPE.RDC_REQUESTS_READ} (no separate write scope for requests).`,
            ],
            [
              "rdcAccounts",
              "RDC accounts",
              `Read: ${BANKING_SCOPE.RDC_ACCOUNTS_READ}. Write: also ${BANKING_SCOPE.RDC_ACCOUNTS_WRITE}.`,
            ],
            [
              "rdcVelocity",
              "RDC velocity",
              `Read: ${BANKING_SCOPE.RDC_VELOCITY_READ}. Write: also ${BANKING_SCOPE.RDC_VELOCITY_WRITE}.`,
            ],
          ] as const
        ).map(([key, label, tip]) => (
          <FieldRow
            key={key}
            htmlFor={`${idPrefix}-rdc-${key}`}
            label={label}
            tooltip={tip}
          >
            <PermissionTierTrack
              labelledBy={`${idPrefix}-rdc-${key}`}
              value={capabilities[key]}
              allowWriteTier={key !== "rdcRequests"}
              onChange={(tier) => onTierChange(key, tier)}
            />
          </FieldRow>
        ))}
      </div>
    </div>
  )
}

export type SilverlakeBankingCapabilitiesPickersProps = BankingPermissionRowProps

export function SilverlakeBankingCapabilitiesPickers({
  idPrefix,
  capabilities,
  onTierChange,
}: SilverlakeBankingCapabilitiesPickersProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium">
              BaaS capabilities
              <span className="text-destructive ml-0.5" aria-hidden>
                *
              </span>
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label="More about BaaS capabilities"
                >
                  <Info className="size-4" strokeWidth={1.5} aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="start"
                className="max-w-xs text-left text-xs leading-snug"
              >
                Each row sets not permitted, read-only, or read+write for that API area. Write includes read at token
                issuance. Maps to OAuth scopes such as{" "}
                <code className="rounded bg-muted px-0.5">{BANKING_SCOPE.CUSTOMER_READ}</code> on{" "}
                <code className="rounded bg-muted px-0.5">/api/oauth/token</code>.
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">JXChange</h4>
          <JxchangeBankingPermissionRows
            idPrefix={idPrefix}
            capabilities={capabilities}
            onTierChange={onTierChange}
          />
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Online Banking Admin (RDC)</h4>
          <AdminApiBankingPermissionRows
            idPrefix={idPrefix}
            capabilities={capabilities}
            onTierChange={onTierChange}
          />
        </div>
      </div>
    </div>
  )
}
